import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { compose } from "recompose";
import { useRouter } from "next/router";
import de from "date-fns/locale/de";
import { registerLocale } from "react-datepicker";
import FilterIcon from "assets/images/svg/filterIcon.svg";
import { searchUsers } from "api";
import { removeOverlay, addOverlay, sideModalScrollHidden } from "utils";
import { useFormattedMessage } from "hooks";
import { umbrellaUsersPage } from "i18n/messages/meta";
import AdminBrokerPageProto from "components/AdminBrokerPageProto";
import FilterComponent from "components/FilterComponent";
import Tile from "components/Admin/Users/Tile";
import WrappedUserSideModal from "components/Umbrella/Users/WrappedUserSideModal";
import Skeleton from "components/Admin/Users/Skeleton";
import Checkbox from "components/Checkbox";
import SelectInput from "components/SelectInput";
import ConfirmationSideModal from "../ConfirmationModal";
import WrappedFilterSideModal from "./WrappedFilterSideModal";
import { selectIsUserAdmin } from "store/user/account/selectors";
import { selectProjectDetails } from "store/project/selectors";
import { selectTenants } from "store/admin/tenant/selectors";
import { updateData } from "store/admin/crm/actions";
import { UMBRELLA_STATUS } from "constants/status";
import { umbrellaUpdateUsers } from "api/FnxtApis";
import LoadMoreFooter, { RecordsLabel } from "components/LoadMoreFooter";
import RecordsLoadingError from "components/LoadMoreFooter/RecordsLoadingError";

registerLocale("de", de);

const messages = {
    allUser: "all_user",
    all: "all",
    filter: "filter",
    clear: "clear",
    actions: "actions",
    statusPending: "provision_pending",
    statusApproved: "approved",
    statusRejected: "rejected",
    statusReclearance: "reclearance",
    approveSuccessMessage: "user_successfully_approved",
    rejectSuccessMessage: "user_successfully_rejected",
    approve: "approve",
    reject: "reject",
    rejectModalQuestion: "really_want_to_reject",
    approveModalQuestion: "approve_following_user",
    rejectSingleUserModalQuestion: "really_want_to_reject_user",
    approveSingleUserModalQuestion: "approve_this_user",
    clearAllFilters: "clear_all_filters",
    yesApprove: "yes_approve",
    yesReject: "reject",
};

const paging = { offset: 0, first: 20 };

const UmbrellaUsersPage = ({ menu }) => {
    const router = useRouter();
    const { formatMessage } = useFormattedMessage();
    const lastRender = useRef(false);
    const [pagination, setPagination] = useState({ ...paging });
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [filterText, setFilterText] = useState("");
    const [selectedStatusUmbrellaClearance, setSelectedStatusUmbrellaClearance] = useState(null);
    const [toggleMultiSelect, setToggleMultiSelect] = useState(false);
    const [checkedUser, setCheckedUser] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);

    const [noRecordsError, setNoRecordsError] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(false);

    const [isShowActions, setShowActions] = useState(false);
    const [filterModalIsOpen, setFilterModalOpen] = useState(false);

    const [isApproving, setApproving] = useState(false);
    const [isRejecting, setRejecting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState();

    const [checkedUserIds, setCheckedUserIds] = useState([]);
    const [isDisableCheckbox, setDisableCheckbox] = useState(false);
    const [isFilterActive, setFilterActive] = useState(false);

    const [isRequestError, setRequestError] = useState(false);
    const [failedUsersList, setFailedUsersList] = useState([]);
    const [succeedUsersList, setSucceedUsersList] = useState([]);

    const [isSelectLoadingUsers, setSelectLoadingUsers] = useState(false);

    const [errorUsersList, setErrorUsersList] = useState();
    const [isSearchActive, setSearchActive] = useState(false);

    // prepare the options for status select
    const statusUmbrellaClearance = [
        {
            value: UMBRELLA_STATUS.LIABILITY_UMBRELLA_PENDING,
            label: formatMessage(messages.statusPending),
        },
        {
            value: UMBRELLA_STATUS.LIABILITY_UMBRELLA_APPROVED,
            label: formatMessage(messages.statusApproved),
        },
        {
            value: UMBRELLA_STATUS.LIABILITY_UMBRELLA_REJECTED,
            label: formatMessage(messages.statusRejected),
        },
        {
            value: UMBRELLA_STATUS.LIABILITY_UMBRELLA_RECLEARANCE,
            label: formatMessage(messages.statusReclearance),
        },
    ];

    // prepare the function for reset api messages
    const resetApiMessages = () => {
        setFailedUsersList([]);
        setSucceedUsersList([]);
        setErrorUsersList();
        setRequestError(false);
    };

    /*
     * prepare the function for grouping error messages
     * it will show when getting error in accept and reject
     */
    const groupApiMessages = (messageList, keyName, heading) => {
        const newGroupMessageList = [];
        let messageKey = "",
            baseObj = [];
        messageList.map((item) => {
            messageKey = `${item.message || heading}`;
            baseObj = newGroupMessageList[messageKey] || [];
            baseObj.push(item[keyName]);
            newGroupMessageList[messageKey] = baseObj;
        });
        return newGroupMessageList;
    };
    /*
     * search user api calling function  with filters
     * update the pagination value
     * set the users collection in component local state
     */
    const fetchUsersData = (_offset, _first, _reset) => {
        setLoading(true);
        if (_reset) {
            setUsers([]);
            setTotal(0);
        }

        searchUsers({
            offset: _offset,
            first: _first,
            status: filterStatus(),
            query: filterText || null,
            showUmbrellaUsers: true,
        })
            .then((res) => {
                if (!res || res.data?.error) {
                    _offset == 0 ? setNoRecordsError(true) : setLoadMoreError(true);
                    return;
                } else {
                    setPagination({ offset: _offset + _first, first: _first });
                }

                if (_reset) {
                    setUsers([...res.data?.users]);
                } else {
                    setUsers([...users, ...res.data?.users]);
                }

                setTotal(res.data?.totalUsers);
                setNoRecordsError(false);
                setLoadMoreError(false);
            })
            .catch((err) => {
                console.error(err, "error");
                _offset == 0 ? setNoRecordsError(true) : setLoadMoreError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // function for approve user api
    const umbrellaApproveRejectUsersStatus = (userIds, isApprove) => {
        isApprove ? setApproving(true) : setRejecting(true);
        umbrellaUpdateUsers({ userIds: userIds || checkedUserIds, isApprove })
            .then((res) => {
                const success = res?.data?.success;
                const failed = res?.data?.failed;

                failed && setFailedUsersList(failed);
                success &&
                    setSucceedUsersList(
                        groupApiMessages(success, "uid", isApprove ? "Successfully Approved" : "Successfully Rejected"),
                    );

                if (!failed && !success) {
                    setRequestError(true);
                } else if (failed?.length <= 0 && success?.length > 0) {
                    setSelectedUserId();
                    setFilterModalOpen(false);
                    removeOverlay();
                    router.push(window.location.pathname);
                    setShowUserModal(false);
                    setToggleMultiSelect(false);
                    setRequestError(false);
                    setCheckedUser([]);
                    fetchUsersData(0, 20, true);
                    setSelectLoadingUsers(false);
                }
            })
            .catch((err) => {
                console.error(err, "error");
                setRequestError(true);
            })
            .finally(() => {
                setApproving(false);
                setRejecting(false);
            });
    };

    // prepare the KYC status value for the user search query
    const filterStatus = () => {
        const _statusUmbrellaClearance = [];
        selectedStatusUmbrellaClearance && _statusUmbrellaClearance.push(selectedStatusUmbrellaClearance?.value);
        return _statusUmbrellaClearance.length ? _statusUmbrellaClearance : null;
    };

    /*
     * function called when page scroll
     * search user function called with pagination
     */
    const onLoadMore = (isReloading) => {
        if (!loading && (users.length < total || isReloading)) {
            fetchUsersData(pagination.offset, pagination.first);
        }
    };

    const rowClicked = (user) => {
        router.push(`${router.pathname}?id=${user.uid}`, `${router.pathname}?id=${user.uid}`);
        setShowUserModal(true);
        sideModalScrollHidden(true);
        resetApiMessages();
        addOverlay();
    };

    const onCloseUserModal = () => {
        router.push(window.location.pathname);
        setShowUserModal(false);
        sideModalScrollHidden(false);
        removeOverlay();
        removeOverlay("admin-overlay");
        resetApiMessages();
        setSelectedUserId();
    };

    // prepare five tile of skeleton to show on loading users collection
    const skeletonModal = () => {
        const _elements = [];
        for (let index = 0; index < 5; index++) {
            _elements[index] = <Skeleton key={`skeleton-${index}`} />;
        }
        return _elements;
    };

    /*
     * called when title checkbox clicked
     * set the user checked or unchecked
     * set the multi select toggle
     */
    const onClickCheckbox = (_userId, _selected, _userName) => {
        const _users = [...checkedUser];
        let _index;
        if (_selected) {
            _users.push({
                id: _userId,
                name: _userName,
            });
            setCheckedUser([..._users]);
        } else {
            _index = checkedUser.findIndex((_user) => _user.id === _userId);
            if (_index !== -1) {
                _users.splice(_index, 1);
                setCheckedUser([..._users]);
            }
        }

        if (_users.length && !toggleMultiSelect) {
            setToggleMultiSelect(true);
            setSelectLoadingUsers(false);
        } else if (!_users.length && toggleMultiSelect) {
            setToggleMultiSelect(false);
            setSelectLoadingUsers(false);
        }
    };

    /*
     * prepare the user uid in collection when click on [] All checkbox
     * empty the checkedUser collection on clicking [] clear checkbox
     * toggle the [] All & [] clear checkbox
     */
    const selectAllUsers = () => {
        const _users = [];
        users.map((_user) => {
            if (_user.StatusUmbrellaClearance === UMBRELLA_STATUS.PENDING) {
                _users.push({
                    id: _user.uid,
                    name: _user.FirstName ? `${_user.FirstName} ${_user.LastName}` : _user.Email,
                });
            }
        });
        setCheckedUser([..._users]);
    };

    const onMultiSelectClicked = () => {
        if (toggleMultiSelect) {
            setCheckedUser([]);
            setToggleMultiSelect(false);
            setSelectLoadingUsers(false);
        } else {
            selectAllUsers();
            setSelectLoadingUsers(true);
            setToggleMultiSelect(true);
        }
    };

    const onRemoveSelectedKycStatus = () => {
        setSelectedStatusUmbrellaClearance(null);
    };

    const onRejectBtnClicked = () => {
        setShowDeleteModal(true);
        addOverlay("admin-overlay");
        sideModalScrollHidden(true);
        resetApiMessages();
    };

    const onApproveBtnClicked = () => {
        setShowApproveModal(true);
        addOverlay("admin-overlay");
        sideModalScrollHidden(true);
        resetApiMessages();
    };

    const clearAllFilters = () => {
        setSelectedStatusUmbrellaClearance(null);
        setFilterText("");
    };

    useEffect(() => {
        if (checkedUser?.length > 0) {
            const checkedUserIds = checkedUser.map((item) => item.id);
            setCheckedUserIds(checkedUserIds);
        }
    }, [checkedUser]);

    useEffect(() => {
        setDisableCheckbox(
            loading ||
                selectedStatusUmbrellaClearance?.value === UMBRELLA_STATUS.LIABILITY_UMBRELLA_APPROVED ||
                selectedStatusUmbrellaClearance?.value === UMBRELLA_STATUS.LIABILITY_UMBRELLA_REJECTED ||
                users.length == 0 ||
                !users.find((item) => item.StatusUmbrellaClearance === UMBRELLA_STATUS.PENDING),
        );
    }, [selectedStatusUmbrellaClearance, loading, users]);

    useEffect(() => {
        selectedStatusUmbrellaClearance?.value || filterText ? setFilterActive(true) : setFilterActive(false);
    }, [selectedStatusUmbrellaClearance, filterText]);

    useEffect(() => {
        fetchUsersData(pagination.offset, pagination.first);
    }, []);

    /*
     * call the fetch user function on changing filter
     * set the initial state of users collection and checkbox toggle
     */
    useEffect(() => {
        if (lastRender.current) {
            setUsers([]);
            setCheckedUser([]);
            setToggleMultiSelect(false);
            fetchUsersData(paging.offset, paging.first, true);
        } else {
            lastRender.current = true;
        }
    }, [selectedStatusUmbrellaClearance, filterText]);

    useEffect(() => {
        if (router.query.id) {
            addOverlay();
            setShowUserModal(true);
            sideModalScrollHidden(true);
        } else {
            setShowUserModal(false);
            sideModalScrollHidden(false);
            removeOverlay();
        }
    }, [router, router?.query, router?.query?.id]);

    useEffect(() => {
        if (users && isSelectLoadingUsers && toggleMultiSelect) {
            selectAllUsers();
        }
    }, [users, isSelectLoadingUsers, toggleMultiSelect]);

    const Modals = () => (
        <>
            <WrappedUserSideModal
                open={showUserModal}
                userId={router.query.id}
                onCloseButtonClick={onCloseUserModal}
                onRejectClicked={onRejectBtnClicked}
                isApproving={isApproving}
                isRejecting={isRejecting}
                onConfirm={onApproveBtnClicked}
                setSelectedUserId={setSelectedUserId}
                requestError={isRequestError}
                errorUsersList={errorUsersList}
            />
            <WrappedFilterSideModal
                open={filterModalIsOpen}
                onCloseButtonClick={() => {
                    setFilterModalOpen(false);
                    resetApiMessages();
                }}
                isShowActions={isShowActions}
                isRejecting={isRejecting}
                isApproving={isApproving}
                loading={loading}
                disabledActionBtn={checkedUser?.length <= 0}
                statusUmbrellaClearance={statusUmbrellaClearance}
                selectedStatusUmbrellaClearance={selectedStatusUmbrellaClearance}
                setSelectedStatusUmbrellaClearance={setSelectedStatusUmbrellaClearance}
                onRejectBtnClicked={onRejectBtnClicked}
                onConfirm={onApproveBtnClicked}
            />
            <ConfirmationSideModal
                open={showDeleteModal}
                onCloseButtonClick={() => {
                    if (
                        (succeedUsersList && Object.entries(succeedUsersList).length > 0) ||
                        (errorUsersList && Object.entries(errorUsersList).length > 0)
                    ) {
                        setCheckedUser([]);
                        setFilterModalOpen(false);
                    }
                    setShowDeleteModal(false);
                    resetApiMessages();
                    removeOverlay("admin-overlay");
                    !showUserModal && sideModalScrollHidden(false);
                }}
                propForConfirm={selectedUserId}
                confirmBtnText={formatMessage(messages.yesReject)}
                successMessage={formatMessage(messages.rejectSuccessMessage)}
                isConfirming={isRejecting}
                question={formatMessage(selectedUserId ? messages.rejectSingleUserModalQuestion : messages.rejectModalQuestion)}
                onConfirm={(userIds) => umbrellaApproveRejectUsersStatus(userIds, false)}
                confirmationList={checkedUser}
                requestError={isRequestError}
                failedList={failedUsersList}
                succeedList={succeedUsersList}
                errorList={errorUsersList}
            />
            <ConfirmationSideModal
                open={showApproveModal}
                onCloseButtonClick={() => {
                    if (
                        (succeedUsersList && Object.entries(succeedUsersList).length > 0) ||
                        (errorUsersList && Object.entries(errorUsersList).length > 0)
                    ) {
                        setCheckedUser([]);
                        setFilterModalOpen(false);
                    }
                    setShowApproveModal(false);
                    resetApiMessages();
                    removeOverlay("admin-overlay");
                    !showUserModal && sideModalScrollHidden(false);
                }}
                propForConfirm={selectedUserId}
                confirmBtnText={formatMessage(messages.yesApprove)}
                successMessage={formatMessage(messages.approveSuccessMessage)}
                isConfirming={isApproving}
                question={formatMessage(selectedUserId ? messages.approveSingleUserModalQuestion : messages.approveModalQuestion)}
                onConfirm={(userIds) => umbrellaApproveRejectUsersStatus(userIds, true)}
                confirmationList={checkedUser}
                requestError={isRequestError}
                failedList={failedUsersList}
                succeedList={succeedUsersList}
                errorList={errorUsersList}
            />
        </>
    );

    return (
        <AdminBrokerPageProto
            headTitle={formatMessage(umbrellaUsersPage.title)}
            menu={menu}
            className="admin-page admin-users-page"
            topChildren={Modals}
        >
            <h4>{formatMessage(messages.allUser)}</h4>
            <div className="mobile admin-mobile-filter">
                <div className="flex align-center justify-between mb-3">
                    <div className="flex">
                        <div
                            className={`flex border-1 border-radius border-another-grey-1 checkbox-user-all cursor-pointer ${
                                isDisableCheckbox ? "disable-checkbox" : ""
                            }`}
                            onClick={() => (isDisableCheckbox ? {} : onMultiSelectClicked())}
                        >
                            <Checkbox
                                isDisabled={
                                    isDisableCheckbox ||
                                    loading ||
                                    selectedStatusUmbrellaClearance === UMBRELLA_STATUS.LIABILITY_UMBRELLA_APPROVED ||
                                    selectedStatusUmbrellaClearance === UMBRELLA_STATUS.LIABILITY_UMBRELLA_REJECTED
                                }
                                className="pointer-none"
                                inputId="all"
                                name="all"
                                size="small"
                                value={toggleMultiSelect}
                                label={formatMessage(toggleMultiSelect ? messages.clear : messages.all)}
                                labelClassName="ml-2 text-14 line-height-normal fw-400"
                            />
                        </div>
                        <button // FILTER BUTTON FOR MOBILE VIEW
                            disabled={loading}
                            className={`text-14 ml-3 flex justify-center align-center filter-action-icon-btn ${
                                loading ? "disable-grey" : ""
                            } ${isSearchActive ? "border-primary" : ""}`}
                            onClick={() => {
                                setSearchActive(!isSearchActive);
                                $(".filter-search-container").slideToggle();
                            }}
                        >
                            <i style={{ color: "#9e9fa4", paddingTop: "2px" }} className="fa fa-search" aria-hidden="true" />
                        </button>
                        <button // FILTER BUTTON FOR MOBILE VIEW
                            disabled={loading}
                            className={`text-14 ml-3 flex justify-center align-center filter-action-icon-btn ${
                                loading ? "disable-grey" : ""
                            }`}
                            onClick={() => {
                                setShowActions(false);
                                setFilterModalOpen(true);
                            }}
                        >
                            <FilterIcon style={{ width: "18px", height: "18px", fill: "#9e9fa4" }} />
                        </button>
                    </div>
                    {/* <button // ACTION BUTTON FOR MOBILE VIEW
						disabled={loading}
						className={`text-14 medium my-0 filter-action-text-btn ${loading ? "disable-grey" : ""}`}
						onClick={() => {
							setShowActions(true);
							setFilterModalOpen(true);
						}}
					>
						{formatMessage(messages.actions)}
					</button> */}
                </div>
                <div className="filter-search-container">
                    <FilterComponent
                        inputClassName="w-100 hf-10"
                        small
                        className=""
                        onFilter={(value) => setFilterText(value)}
                        onClear={() => setFilterText("")}
                        filterText={filterText}
                        // isShowSearch={false}
                    />
                </div>

                <div className="filtered-values-container flex flex-wrap">
                    <div className="flex justify-start w-100">
                        <button
                            disabled={loading || isApproving || checkedUser?.length <= 0 || showApproveModal}
                            className={`export-secondary text-14 mr-3 secondary flex justify-center mt-0 align-center ${
                                loading || isApproving || checkedUser?.length <= 0 ? "disable-grey" : ""
                            }`}
                            onClick={onApproveBtnClicked}
                        >
                            {formatMessage(messages.approve)}
                        </button>
                        <button
                            disabled={loading || isRejecting || checkedUser?.length <= 0 || showDeleteModal}
                            onClick={onRejectBtnClicked}
                            className={`export-secondary text-14 mr-3 secondary flex justify-center mt-0 align-center ${
                                loading || isRejecting || checkedUser?.length <= 0 ? "disable-grey" : ""
                            }`}
                        >
                            {formatMessage(messages.reject)}
                        </button>
                    </div>
                    {selectedStatusUmbrellaClearance ? (
                        <div className="tag-container w-100 mt-4">
                            <div className="selected-tag mb-0 text-12 mr-3">
                                {selectedStatusUmbrellaClearance?.label}
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    className="cursor-pointer"
                                    onClick={() => onRemoveSelectedKycStatus()}
                                >
                                    <defs>
                                        <clipPath id="clip-path">
                                            <rect
                                                id="Rectangle_3915"
                                                data-name="Rectangle 3915"
                                                width="12"
                                                height="12"
                                                transform="translate(-0.764 -0.763)"
                                                fill="#fff"
                                                stroke="#707070"
                                                strokeWidth="1"
                                            />
                                        </clipPath>
                                    </defs>
                                    <g
                                        id="Mask_Group_70"
                                        data-name="Mask Group 70"
                                        transform="translate(0.764 0.763)"
                                        clipPath="url(#clip-path)"
                                    >
                                        <g id="Group_10940" data-name="Group 10940" transform="translate(1.424 1.425)">
                                            <no
                                                id="Path_5635"
                                                data-name="Path 5635"
                                                d="M-23457-22296.006l7.623,7.627"
                                                transform="translate(23457 22296.006)"
                                                fill="none"
                                                stroke="#171926"
                                                strokeWidth="1.5"
                                            />
                                            <path
                                                id="Path_5636"
                                                data-name="Path 5636"
                                                d="M-23457-22288.379l7.623-7.627"
                                                transform="translate(23457 22296.006)"
                                                fill="none"
                                                stroke="#171926"
                                                strokeWidth="1.5"
                                            />
                                        </g>
                                    </g>
                                </svg>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="desktop">
                <label className="text-16 flex-1">{formatMessage(messages.filter)}:</label>
                {isFilterActive ? (
                    <label className="another-grey-color cursor-pointer ml-2" onClick={() => clearAllFilters()}>
                        {formatMessage(messages.clearAllFilters)}
                    </label>
                ) : null}
                <div className="flex mt-8 justify-start w-100">
                    <div
                        className={`flex border-1 mr-3 border-radius border-another-grey-1 checkbox-user-all cursor-pointer ${
                            isDisableCheckbox ? "disable-checkbox" : ""
                        }`}
                        onClick={() => (isDisableCheckbox ? {} : onMultiSelectClicked())}
                    >
                        <Checkbox
                            isDisabled={
                                isDisableCheckbox ||
                                loading ||
                                selectedStatusUmbrellaClearance === UMBRELLA_STATUS.LIABILITY_UMBRELLA_APPROVED ||
                                selectedStatusUmbrellaClearance === UMBRELLA_STATUS.LIABILITY_UMBRELLA_REJECTED
                            }
                            className="pointer-none"
                            inputId="all"
                            name="all"
                            size="small"
                            value={toggleMultiSelect}
                            label={formatMessage(toggleMultiSelect ? messages.clear : messages.all)}
                            labelClassName="ml-2 text-14 line-height-normal fw-400"
                        />
                    </div>
                    <FilterComponent
                        small
                        className="mr-5 mw-160"
                        onFilter={(value) => setFilterText(value)}
                        onClear={() => setFilterText("")}
                        filterText={filterText}
                        disabled={loading}
                    />
                    <SelectInput
                        className="mb-0 mr-3 mw-160 w-100 filter-select-users"
                        name="investment_status"
                        options={statusUmbrellaClearance}
                        placeholder="Status"
                        isSearchable={false}
                        disabled={loading}
                        isClearable
                        selectClassName="small-menu-list"
                        value={selectedStatusUmbrellaClearance}
                        onChange={(value) => setSelectedStatusUmbrellaClearance(value)}
                    />
                </div>

                <RecordsLabel className="mt-3" totalRecords={total} fetched={users.length} />

                <div className="flex justify-start w-100 mt-8">
                    <button
                        disabled={loading || isApproving || checkedUser?.length <= 0 || showApproveModal}
                        className={`export-secondary text-14 mr-3 secondary flex justify-center mt-0 align-center ${
                            loading || isApproving || checkedUser?.length <= 0 ? "disable-grey" : ""
                        }`}
                        onClick={onApproveBtnClicked}
                    >
                        {formatMessage(messages.approve)}
                    </button>
                    <button
                        disabled={loading || isRejecting || checkedUser?.length <= 0 || showDeleteModal}
                        onClick={onRejectBtnClicked}
                        className={`export-secondary text-14 mr-3 secondary flex justify-center mt-0 align-center ${
                            loading || isRejecting || checkedUser?.length <= 0 ? "disable-grey" : ""
                        }`}
                    >
                        {formatMessage(messages.reject)}
                    </button>
                </div>
            </div>
            {users.map((user, index) => {
                return (
                    <Tile
                        isUmbrella
                        key={"user-" + index}
                        user={user}
                        onClick={rowClicked}
                        selected={checkedUser.findIndex((_user) => _user.id === user.uid) !== -1}
                        onClickCheckbox={onClickCheckbox}
                    />
                );
            })}
            {loading && skeletonModal()}
            <LoadMoreFooter
                loading={loading}
                fetched={users.length}
                totalRecords={total}
                loadingLimit={paging.first}
                onClick={onLoadMore}
                error={loadMoreError}
            />
            <RecordsLoadingError show={noRecordsError && !loading} onClick={onLoadMore} />
        </AdminBrokerPageProto>
    );
};

const mapStateToProps = createStructuredSelector({
    isAdmin: selectIsUserAdmin,
    projects: selectProjectDetails,
    tenants: selectTenants,
});

const mapDispatchToProps = (dispatch) => ({
    updateData: (data) => dispatch(updateData(data)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(UmbrellaUsersPage);
